import { useEffect, useRef } from 'react';
import { isTouchDevice } from 'utils/helper';

const SHAKE_PEAK_THRESHOLD = 10; // m/s², gravity-free acceleration per axis sum
const SHAKE_PEAK_DEBOUNCE_MS = 100;
const SHAKE_PEAKS_REQUIRED = 3;
const SHAKE_WINDOW_MS = 1500;
const SHAKE_COOLDOWN_MS = 4000;
const SENSOR_SILENCE_GRACE_MS = 4000;

type MotionEventWithPermission = typeof DeviceMotionEvent & {
  requestPermission?: () => Promise<'granted' | 'denied'>;
};

export type MotionSensorState =
  | 'unsupported' // not a touch device or no DeviceMotionEvent API
  | 'pending' // iOS: waiting for a tap to show the permission prompt
  | 'denied' // iOS: user declined the permission prompt
  | 'listening' // subscribed, no sensor reading received yet
  | 'silent' // subscribed but no readings arrive (e.g. insecure http origin)
  | 'active'; // sensor readings are coming in

let motionSensorState: MotionSensorState = 'unsupported';

export const getMotionSensorState = () => motionSensorState;

/**
 * Fires `onShake` when a touch device is shaken. On iOS 13+ motion data
 * requires a permission prompt that can only be opened from a user gesture,
 * so the permission is requested on taps until the user makes a choice.
 */
const useShakeGesture = (onShake: () => void) => {
  const onShakeRef = useRef(onShake);
  onShakeRef.current = onShake;

  useEffect(() => {
    if (typeof window === 'undefined' || !isTouchDevice()) return;
    if (typeof DeviceMotionEvent === 'undefined') return;

    let peakTimes: number[] = [];
    let lastPeakAt = 0;
    let cooldownUntil = 0;
    let lastGravityVector: { x: number; y: number; z: number } | null = null;
    let silenceTimer: ReturnType<typeof window.setTimeout> | null = null;

    const registerMagnitude = (magnitude: number) => {
      const now = Date.now();

      if (now < cooldownUntil) return;
      if (magnitude < SHAKE_PEAK_THRESHOLD) return;
      if (now - lastPeakAt < SHAKE_PEAK_DEBOUNCE_MS) return;

      lastPeakAt = now;
      peakTimes = [...peakTimes.filter((t) => now - t < SHAKE_WINDOW_MS), now];

      if (peakTimes.length >= SHAKE_PEAKS_REQUIRED) {
        peakTimes = [];
        cooldownUntil = now + SHAKE_COOLDOWN_MS;
        onShakeRef.current();
      }
    };

    const handleMotion = (event: DeviceMotionEvent) => {
      motionSensorState = 'active';
      if (silenceTimer) {
        clearTimeout(silenceTimer);
        silenceTimer = null;
      }

      const { acceleration, accelerationIncludingGravity } = event;

      if (
        acceleration?.x !== null &&
        acceleration?.x !== undefined &&
        acceleration.y !== null &&
        acceleration.z !== null
      ) {
        registerMagnitude(
          Math.hypot(acceleration.x, acceleration.y ?? 0, acceleration.z ?? 0),
        );
        return;
      }

      // Some Android browsers only provide the gravity-including reading;
      // the delta between consecutive readings filters gravity out.
      const g = accelerationIncludingGravity;
      if (g?.x === null || g?.x === undefined) return;

      const current = { x: g.x, y: g.y ?? 0, z: g.z ?? 0 };
      if (lastGravityVector) {
        registerMagnitude(
          Math.hypot(
            current.x - lastGravityVector.x,
            current.y - lastGravityVector.y,
            current.z - lastGravityVector.z,
          ),
        );
      }
      lastGravityVector = current;
    };

    const startListening = () => {
      motionSensorState = 'listening';
      window.addEventListener('devicemotion', handleMotion);
      // Insecure origins and some webviews accept the listener but never
      // deliver readings — surface that in the diagnosis report.
      silenceTimer = window.setTimeout(() => {
        if (motionSensorState === 'listening') motionSensorState = 'silent';
      }, SENSOR_SILENCE_GRACE_MS);
    };

    const supportsPermissionPrompt =
      typeof (DeviceMotionEvent as MotionEventWithPermission)
        .requestPermission === 'function';

    const requestPermissionOnTap = () => {
      // Must be invoked on the interface — a detached reference throws.
      (DeviceMotionEvent as MotionEventWithPermission)
        .requestPermission!()
        .then((state) => {
          if (state === 'granted') {
            window.removeEventListener('pointerdown', requestPermissionOnTap);
            startListening();
          } else {
            motionSensorState = 'denied';
            window.removeEventListener('pointerdown', requestPermissionOnTap);
          }
        })
        .catch(() => {
          // The tap was not accepted as a user gesture (or the prompt could
          // not be shown) — keep retrying on the next tap.
        });
    };

    if (supportsPermissionPrompt) {
      motionSensorState = 'pending';
      window.addEventListener('pointerdown', requestPermissionOnTap);
    } else {
      startListening();
    }

    return () => {
      if (silenceTimer) clearTimeout(silenceTimer);
      window.removeEventListener('devicemotion', handleMotion);
      window.removeEventListener('pointerdown', requestPermissionOnTap);
    };
  }, []);
};

export default useShakeGesture;
